import { addCompoundOperation } from '../../../../../offline/offlineCompoundQueue';
import { captureLocalImages } from '../../../../../offline/offlineImageStore';
import { screenFormContext } from '../../../../../offline/formStorage';
import {
  formAccidentNaming,
  directoryNaming,
} from '../../../../../components/forms/AddBasicInfoAccidentForm/constants';
import { formVehicleNaming } from '../../../../../components/forms/AddVehicleForm/constants';
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { OrderType } from '../../../../../types/orders';
import {
  accidentCreationPhotoNames,
  accidentCreationSketch,
  readAccidentCreationCopy,
  releaseAccidentCreationDraft,
} from '../../../../../utils/accidentCreationDraft';
import { draftSketchFileName } from '../../../../../utils/sketchFile';

/**
 * Encola la creación ENTERA del accidente en la cola compound (spec
 * 2026-09-08-fase2-encolada § 3.6): snapshot de los dos formularios, los campos
 * del `FormData` del paso terminal y las fotos de OPFS. Único productor: la
 * página ya no encola por su cuenta.
 *
 * `createClientOpId` es el del intento con red (generado al entrar en el paso
 * terminal). Si el `SAVE_ACCIDENT` se abortó por el plazo pero llegó al
 * servidor, el replay lo reutiliza y el backend devuelve el accidente ya
 * creado en vez de duplicarlo (D3).
 */
export async function queueAccidentCreate({
  formData,
  createClientOpId,
}: {
  formData: FormData;
  createClientOpId: string;
}): Promise<{ redirectTo: string }> {
  const form = JSON.parse(
    decompressSessionStorage(
      sessionStorage.getItem(formAccidentNaming.NEW_ACCIDENT_FORM) as string,
    ),
  );

  const sessionSnapshots: Record<string, string | null> = {};
  [...Object.values(formAccidentNaming), ...Object.values(formVehicleNaming)].forEach((key) => {
    sessionSnapshots[key] = sessionStorage.getItem(key);
  });
  // Campos del FormData del paso terminal: el replay reconstruye el mock con
  // estas tres claves (`syncAccident`).
  sessionSnapshots['_formData_damages'] = (formData.get('damages') as string) || '';
  sessionSnapshots['_formData_involvedVehiclesSwitch'] =
    (formData.get('involvedVehiclesSwitch') as string) || 'false';
  sessionSnapshots['_formData_vehicles'] = (formData.get('vehicles') as string) || '{}';

  // 🔴 Solo las fotos y el croquis de ESTE borrador (spec 2026-11-02
  // wizard-draft-ownership § 6; revisión de la Task 7): las carpetas
  // `accident` y `canvas-accident` las comparten las pestañas y la edición de
  // accidentes. Antes la operación se llevaba las dos carpetas enteras —fotos
  // y croquis de otros borradores viajaban en IndexedDB— y el reintento subía
  // «todo» y «el primer croquis». El reintento sube por la misma lista (la
  // copia del paso 2 va en la foto de sesión).
  const basicInfo = readAccidentCreationCopy(formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM);
  const photoNames = new Set(accidentCreationPhotoNames(basicInfo));
  const sketchFileName = await draftSketchFileName(
    accidentCreationSketch(basicInfo),
    screenFormContext.images,
    directoryNaming.CANVAS,
  );
  const localImages = (
    await captureLocalImages([directoryNaming.CANVAS, OrderType.Accident])
  ).filter((image) =>
    image.directory === directoryNaming.CANVAS
      ? image.fileName === sketchFileName
      : photoNames.has(image.fileName),
  );

  const isCampaignParent = form.isCampaignParent === 'true';
  await addCompoundOperation({
    type: 'accident',
    parentOrderId: form.orderParentId as string,
    parentOrderRoid: form.orderParentRoid as string,
    isCampaignParent,
    sessionSnapshots,
    localImages,
    createClientOpId,
  });

  // Solo lo de este alta (ya va en la operación): sus copias, sus fotos y su
  // croquis, por nombre. Antes `removeNewAccidentStorage` vaciaba las dos
  // carpetas compartidas (Ruling 5).
  await releaseAccidentCreationDraft(screenFormContext, form.orderParentId, { sketchFileName });

  return {
    redirectTo: `${isCampaignParent ? '/campaigns' : '/work-orders'}/${form.orderParentId}`,
  };
}
